import { useApi } from '@/api';
import { ChartSkeleton } from '@/components';
import { useChartTooltip } from '@/hooks';
import {
  DoughnutChart,
  Flex,
  formatPercentage,
  textShortener
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export default function VaultsDonutChart({
  ilk,
  type
}: {
  ilk: string;
  type: string;
}) {
  const [data, error] = useApi({
    url: `/groups/core/ilks/${ilk}/cdps/share/`,
    queryParams: {
      type
    }
  });
  const navigate = useNavigate();

  if (error) {
    throw error;
  }

  const tooltipData = useMemo(() => {
    if (data) {
      return data.map((row) => ({
        ...row,
        urn: textShortener(row.urn)
      }));
    }
    return [];
  }, [data]);

  const customTooltip = useChartTooltip(tooltipData, 'urn', 'share', false);

  const chartData = useMemo(() => {
    if (data) {
      return {
        labels: data.map((row) => textShortener(row.urn)),
        datasets: [
          {
            data: data.map((row) => parseFloat(row.share)),
            hoverOffset: 30
          }
        ]
      };
    }
    return null;
  }, [data]);

  const mapping = useMemo(() => {
    if (data) {
      return data.reduce((acc, row) => {
        const key = textShortener(row.urn);
        acc[key] = row.urn !== 'others' ? `cdps/${row.urn}` : 'cdps/';
        return acc;
      }, {});
    }
    return {};
  }, [data]);

  const customConfig = useMemo(
    () => ({
      options: {
        aspectRatio: 1.8,
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            enabled: false,
            position: 'nearest',
            external: customTooltip
          }
        }
      }
    }),
    [customTooltip]
  );

  if (data) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingTop="m"
        width="100%">
        <DoughnutChart
          // @ts-ignore
          config={customConfig}
          data={chartData}
          valueFormatter={formatPercentage}
          url={`/collateral/seal-engine`}
          navigate={navigate}
          mapping={mapping}
        />
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      paddingTop="m"
      width="100%">
      <ChartSkeleton type="doughnut" paddingTop="m" />
    </Flex>
  );
}
